import { useState } from 'react';
import {
  Info,
  MoreVertical,
  Move,
  PencilLine,
  Trash2,
} from 'lucide-react';
import { BreakIcon } from '@/components/timetable/BreakIcon';
import { breakKindOf } from '@/components/timetable/breakKind';

import { Menu } from '@/components/ui/Menu';
import { periodRange, periodTitle, WEEKDAY_SHORT } from '@/features/teacher/components/timetable/timetableCopy';
import { capitalizeWords } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Holiday } from '@/types/holiday';
import type { TimetablePeriod, Weekday } from '@/types/timetable';
import type { SectionWeekEntry } from '@/types/timetableAdmin';
import { subjectTheme } from '../subjectTheme';
import { examHoldsPeriod, type ExamHours } from '../timetableWeek';

/** One column of the grid: the weekday, and the date it falls on this week. */
export interface WeekColumn {
  day: Weekday;
  /** `YYYY-MM-DD` — what makes the column a date rather than just a name. */
  date: string;
  dayNumber: number;
  monthShort: string;
  isToday: boolean;
  /** The declared closure on that date, or null on an ordinary day. */
  holiday: Holiday | null;
  /** Whether this section sits an institute paper on that date. */
  hasExam: boolean;
  /** Those papers' timings — the periods they overlap take no regular lesson. */
  examHours: ExamHours[];
}

export interface TimetableWeekGridProps {
  columns: WeekColumn[];
  periods: TimetablePeriod[];
  /** Every lesson in this section's week — the grid finds its own cells. */
  entries: SectionWeekEntry[];
  /** Whether a lesson survives the subject and teacher filters. */
  matchesFilter: (entry: SectionWeekEntry) => boolean;
  /** True while any filter is narrowing the grid, so cells can say so. */
  isFiltered: boolean;
  /** The lesson being carried to another cell, or null. */
  moving: SectionWeekEntry | null;
  isSavingMove: boolean;
  onStartMove: (entry: SectionWeekEntry) => void;
  onCancelMove: () => void;
  onDropInto: (day: Weekday, period: TimetablePeriod) => void;
  onPlace: (day: Weekday, period: TimetablePeriod) => void;
  onEditEntry: (entry: SectionWeekEntry) => void;
  onRemoveEntry: (entry: SectionWeekEntry) => void;
  onEditPeriod: (period: TimetablePeriod) => void;
  onRemovePeriod: (period: TimetablePeriod) => void;
}

/**
 * The recurring week, as the office builds it.
 *
 * Every cell here comes from `timetable_entries`. The grid used to fall back to
 * five invented Mathematics lessons whenever a section had none, so a brand new
 * institute was shown a timetable it had not made and could not edit — the
 * empty grid is now the honest answer, and each free cell offers to fill itself.
 *
 * The week is recurring, so what a *date* adds is the institute's calendar: a
 * column whose date is a declared holiday says so and takes no lessons, because
 * the entries repeat all year and know nothing about Dussehra.
 */
export function TimetableWeekGrid({
  columns,
  periods,
  entries,
  matchesFilter,
  isFiltered,
  moving,
  isSavingMove,
  onStartMove,
  onCancelMove,
  onDropInto,
  onPlace,
  onEditEntry,
  onRemoveEntry,
  onEditPeriod,
  onRemovePeriod,
}: TimetableWeekGridProps) {
  const [dropCell, setDropCell] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-4 print:w-full print:max-w-none print:block print:gap-2">

      {moving && (
        <div className="rounded-2xl bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800/80 px-4 py-3 text-sm font-bold text-blue-700 dark:text-blue-300 flex items-center justify-between gap-3 print:hidden">
          <span>
            Moving <strong>{capitalizeWords(moving.subject.name)}</strong> — click a free slot, or press Escape to
            cancel.
          </span>
          <button
            type="button"
            onClick={onCancelMove}
            className="underline text-blue-600 dark:text-blue-200 cursor-pointer text-sm shrink-0"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="rounded-3xl bg-white dark:bg-[#111c2e] border-2 border-slate-200 dark:border-slate-800 shadow-md overflow-hidden print:rounded-none print:border print:border-slate-300 print:shadow-none print:bg-white print:overflow-visible print:w-full print:max-w-none">
        <div className="w-full overflow-x-auto custom-scrollbar print:overflow-visible print:w-full print:max-w-none">
          <table className="w-full table-fixed border-collapse text-sm min-w-[960px] print:min-w-0 print:w-full print:table-fixed print:text-xs">
            <caption className="sr-only">
              The weekly timetable: one row per period, one column per working day.
            </caption>
            <thead>
              <tr className="bg-slate-100 dark:bg-[#0b1322] border-b border-slate-200 dark:border-slate-800 text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300 print:bg-slate-50 print:text-black print:border-b print:border-slate-300">
                <th
                  scope="col"
                  className="px-4 py-3.5 text-left font-bold w-32 border-r border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-[#0b1322] print:w-28 print:px-2.5 print:py-2 print:bg-slate-50 print:text-black print:border-r print:border-slate-300"
                >
                  <div
                    className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300 print:text-black"
                    title="Bell times for the whole institute. Editing one changes every class."
                  >
                    <span className="font-bold tracking-wide text-[14px]">Period</span>
                    <Info className="h-3.5 w-3.5 inline text-slate-400 print:hidden" />
                  </div>
                </th>

                {columns.map((column) => (
                  <th
                    key={column.day}
                    scope="col"
                    className={cn(
                      'px-3 py-2.5 text-center font-bold border-r border-slate-200 dark:border-slate-800/80 last:border-r-0 transition-colors print:px-2 print:py-2 print:border-r print:border-slate-300 print:bg-slate-50 print:text-black',
                      column.isToday && 'bg-slate-200/90 dark:bg-slate-800/80 print:bg-slate-50',
                      column.holiday && !column.isToday && 'bg-rose-50/70 dark:bg-rose-950/50 print:bg-rose-50/50',
                      !column.isToday && !column.holiday && 'bg-slate-100 dark:bg-[#0b1322]',
                    )}
                  >
                    <div
                      className={cn(
                        'font-black text-sm print:text-xs print:text-black',
                        column.isToday && 'text-slate-900 dark:text-white',
                        column.holiday && !column.isToday && 'text-rose-600 dark:text-rose-400',
                        !column.isToday && !column.holiday && 'text-slate-900 dark:text-slate-100',
                      )}
                    >
                      {WEEKDAY_SHORT[column.day]}
                    </div>
                    <div
                      className={cn(
                        'text-xs font-bold mt-0.5 print:text-[10px] print:text-slate-700',
                        column.isToday && 'text-slate-600 dark:text-slate-300',
                        column.holiday && !column.isToday && 'text-rose-500 dark:text-rose-400',
                        !column.isToday && !column.holiday && 'text-slate-500',
                      )}
                    >
                      {column.dayNumber} {column.monthShort}
                    </div>
                    {column.holiday && (
                      <div
                        className="text-[10px] font-bold text-rose-500 dark:text-rose-400 truncate mt-0.5 print:text-rose-700 print:whitespace-normal"
                        title={column.holiday.name}
                      >
                        {column.holiday.name}
                      </div>
                    )}
                    {column.hasExam && (
                      <div className="text-[10px] font-bold text-amber-600 dark:text-amber-400 mt-0.5 print:text-amber-700">
                        Exam day
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-800 print:divide-y print:divide-slate-200">
              {periods.map((period) => {
                if (period.kind === 'BREAK') {
                  const label = periodTitle(period);
                  const breakKind = breakKindOf(label);

                  return (
                    <tr
                      key={period.uuid}
                      className="bg-[#f0f4ff] dark:bg-[#0c162c] border-y-2 border-indigo-200 dark:border-indigo-800/80 print:border-y print:bg-indigo-50/40 print:border-slate-300"
                    >
                      <th
                        scope="row"
                        className="px-4 py-2.5 text-left bg-[#e8efff] dark:bg-[#09101d] border-r border-slate-200 dark:border-slate-800 print:px-2.5 print:py-1.5 print:bg-indigo-50/70 print:border-r print:border-slate-300"
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-xs font-black text-indigo-700 dark:text-indigo-300 print:text-black print:text-[11px]">
                            {periodRange(period.startTime, period.endTime)}
                          </span>
                          <div className="flex items-center gap-1 print:hidden">
                            <button
                              type="button"
                              title={`Edit ${label}`}
                              aria-label={`Edit ${label}`}
                              onClick={() => onEditPeriod(period)}
                              className="text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 p-0.5 transition-colors cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                            >
                              <PencilLine className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              title={`Remove ${label}`}
                              aria-label={`Remove ${label}`}
                              onClick={() => onRemovePeriod(period)}
                              className="text-slate-400 hover:text-rose-500 p-0.5 transition-colors cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </th>
                      <td colSpan={columns.length} className="px-4 py-2.5 text-center print:px-2 print:py-1.5">
                        <div className="inline-flex items-center justify-center gap-1.5 text-xs sm:text-sm font-black text-indigo-800 dark:text-indigo-200 print:text-black print:text-xs">
                          <span>{label}</span>
                          <BreakIcon
                            label={label}
                            className={cn(
                              'h-4 w-4 inline print:h-3.5 print:w-3.5',
                              breakKind === 'LUNCH' ? 'text-amber-500' : 'text-indigo-600',
                            )}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={period.uuid}
                    className="hover:bg-slate-50/60 dark:hover:bg-[#142033]/30 transition-colors print:border-slate-300"
                  >
                    <th
                      scope="row"
                      className="px-4 py-3 align-top text-left bg-slate-50/80 dark:bg-[#0b1322]/80 border-r border-slate-200 dark:border-slate-800 print:px-2.5 print:py-2 print:bg-slate-50/60 print:border-r print:border-slate-300"
                    >
                      <div className="flex flex-col gap-1 print:gap-0.5">
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-black text-sm sm:text-base text-slate-900 dark:text-white truncate print:text-xs print:font-extrabold print:text-black print:overflow-visible print:whitespace-normal">
                            {/* {period.label ?? period.periodNumber} */}
                           P - { period.periodNumber}
                          </span>
                          <div className="flex items-center gap-1 shrink-0 print:hidden">
                            <button
                              type="button"
                              title={`Edit ${periodTitle(period)}`}
                              aria-label={`Edit ${periodTitle(period)}`}
                              onClick={() => onEditPeriod(period)}
                              className="text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 p-0.5 transition-colors cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                            >
                              <PencilLine className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              title={`Remove ${periodTitle(period)}`}
                              aria-label={`Remove ${periodTitle(period)}`}
                              onClick={() => onRemovePeriod(period)}
                              className="text-slate-400 hover:text-rose-600 p-0.5 transition-colors cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        <span className="text-[11px] text-slate-500 dark:text-slate-400 font-bold whitespace-nowrap print:text-[10px] print:text-slate-700">
                          {periodRange(period.startTime, period.endTime)}
                        </span>
                      </div>
                    </th>

                    {columns.map((column) => {
                      const entry = entries.find(
                        (item) =>
                          item.dayOfWeek === column.day && item.period.uuid === period.uuid,
                      );
                      const cellKey = `${column.day}-${period.uuid}`;
                      const isSource = Boolean(entry) && moving?.uuid === entry?.uuid;
                      const isCarrying = Boolean(moving) && !isSource;
                      const isHiddenByFilter = entry ? !matchesFilter(entry) : false;
                      const theme = entry ? subjectTheme(entry.subject.name) : null;

                      return (
                        <td
                          key={cellKey}
                          onDragOver={(event) => {
                            if (!isCarrying || entry) return;
                            event.preventDefault();
                            setDropCell(cellKey);
                          }}
                          onDragLeave={() =>
                            setDropCell((current) => (current === cellKey ? null : current))
                          }
                          onDrop={(event) => {
                            if (!isCarrying || entry) return;
                            event.preventDefault();
                            setDropCell(null);
                            onDropInto(column.day, period);
                          }}
                          className={cn(
                            'p-1.5 align-top border-r border-slate-200 dark:border-slate-800/80 last:border-r-0 transition-colors print:p-1 print:border-r print:border-slate-300',
                            column.isToday && 'bg-indigo-50/60 dark:bg-[#0f1d35]/60 print:bg-indigo-50/20',
                            column.holiday && !column.isToday && 'bg-rose-50/50 dark:bg-[#250d18]/50 print:bg-rose-50/20',
                            dropCell === cellKey && 'bg-indigo-100/70 dark:bg-indigo-900/40',
                          )}
                        >
                          {entry ? (
                            <div
                              draggable={!isSavingMove}
                              onDragStart={(event) => {
                                event.dataTransfer.setData('text/plain', entry.uuid);
                                onStartMove(entry);
                              }}
                              onDragEnd={onCancelMove}
                              className={cn(
                                'group relative flex flex-col justify-center gap-1 rounded-xl p-2 border transition-all cursor-grab active:cursor-grabbing select-none shadow-xs min-h-[60px] print:min-h-[48px] print:p-1.5 print:rounded-lg print:shadow-none print:border-slate-300 print:text-black print:overflow-hidden print:w-full',
                                theme?.cardBg,
                                isSource && 'ring-2 ring-indigo-500 scale-[0.98]',
                                isHiddenByFilter && 'opacity-25 grayscale print:opacity-100 print:grayscale-0',
                              )}
                            >
                              <div className="flex items-start justify-between gap-1 min-w-0">
                                <div className="flex items-center gap-1.5 min-w-0 print:gap-1">
                                  <span
                                    className={cn('p-1.5 rounded-lg shrink-0 shadow-2xs print:p-0.5 print:rounded print:shadow-none', theme?.iconBg)}
                                  >
                                    {theme?.icon}
                                  </span>
                                  <div className="flex flex-col min-w-0 justify-center overflow-hidden">
                                    <span
                                      className="font-black text-[12px] leading-tight truncate print:text-[10px] print:font-extrabold print:truncate print:text-black min-w-0 capitalize"
                                      title={capitalizeWords(entry.subject.name)}
                                    >
                                      {entry.subject.name}
                                    </span>
                                    <span
                                      className="text-[11px] opacity-90 truncate font-bold leading-tight min-w-0 pt-1 print:pt-0.5 print:text-[9px] print:truncate print:text-slate-700 print:opacity-100"
                                      title={entry.teacher ?? 'Nobody is allotted this subject yet'}
                                    >
                                      {entry.teacher ?? 'Unallotted'}
                                    </span>
                                  </div>
                                </div>

                                <div className="shrink-0 self-start print:hidden">
                                  <Menu
                                    label={`Options for ${capitalizeWords(entry.subject.name)}`}
                                    triggerClassName="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors cursor-pointer rounded-lg border-0 bg-transparent shadow-none"
                                    trigger={<MoreVertical className="h-3.5 w-3.5" aria-hidden />}
                                    items={[
                                      {
                                        key: 'edit',
                                        label: 'Edit slot',
                                        icon: <PencilLine className="h-3.5 w-3.5 text-slate-500" />,
                                        onSelect: () => onEditEntry(entry),
                                      },
                                      {
                                        key: 'move',
                                        label: 'Move to another slot',
                                        icon: <Move className="h-3.5 w-3.5 text-slate-500" />,
                                        onSelect: () => onStartMove(entry),
                                      },
                                      {
                                        key: 'delete',
                                        label: 'Delete slot',
                                        tone: 'danger',
                                        icon: <Trash2 className="h-3.5 w-3.5" />,
                                        onSelect: () => onRemoveEntry(entry),
                                      },
                                    ]}
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              title={
                                column.examHours.some((exam) => examHoldsPeriod(exam, period))
                                  ? 'An institute exam is scheduled in this period'
                                  : isCarrying
                                    ? `Move ${capitalizeWords(moving?.subject.name ?? '')} here`
                                    : `Place a lesson in ${WEEKDAY_SHORT[column.day]}, ${periodTitle(period)}`
                              }
                              onClick={() =>
                                isCarrying ? onDropInto(column.day, period) : onPlace(column.day, period)
                              }
                              className={cn(
                                'w-full h-full min-h-[60px] rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 hover:border-indigo-500 hover:bg-indigo-50/40 dark:hover:bg-indigo-950/30 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center justify-center transition-all cursor-pointer font-bold text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 print:min-h-[48px] print:border-slate-200 print:border-solid print:bg-transparent print:hover:bg-transparent',
                                isCarrying &&
                                  'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-xs',
                                isFiltered && !isCarrying && 'opacity-40 print:opacity-100',
                              )}
                            >
                              <span className="print:hidden">{isCarrying ? 'Drop here' : '+'}</span>
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
